import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  computed,
  inject,
  Injector,
  input,
  linkedSignal,
  viewChild,
} from '@angular/core';
import { FieldTree } from '@angular/forms/signals';
import { SvgIconComponent } from 'angular-svg-icon';
import { MobilePortraitService } from '../../../../services/mobile-portrait.service';
import {
  NumericKeypadService,
  NumericKeypadTarget,
} from './numeric-keypad.service';
import {
  findScrollableAncestor,
  prefersReducedMotion,
  scrollElementIntoView,
} from '../../../../utils/scroll-into-view.utils';

@Component({
  selector: 'app-number-input',
  imports: [SvgIconComponent],
  templateUrl: './number-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:pointerdown)': 'onDocumentPointerDown($event)',
    // Keeps a caller's static `id` attribute off the host element, which would
    // otherwise duplicate the `id` the template below puts on the real input.
    '[attr.id]': 'null',
  },
})
export class NumberInputComponent implements OnDestroy {
  readonly field = input.required<FieldTree<number | null>>();
  readonly id = input('');
  readonly label = input('');
  readonly hideLabel = input(false);
  readonly placeholder = input('');
  readonly containerClass = input('');
  readonly suffixText = input('');
  readonly errorMessages = input<Record<string, string>>({});

  private readonly inputElRef =
    viewChild<ElementRef<HTMLInputElement>>('inputEl');
  private readonly fieldRootRef =
    viewChild<ElementRef<HTMLElement>>('fieldRoot');

  readonly isMobilePortrait = inject(MobilePortraitService).isMobilePortrait;
  private readonly keypad = inject(NumericKeypadService);
  private readonly injector = inject(Injector);

  protected readonly state = computed(() => this.field()());

  protected readonly labelText = computed(() =>
    this.state().required() ? `${this.label()} *` : this.label()
  );

  // Recomputes from the field's value on every external write but still keeps the
  // previous text when the value is the same, so a leading or trailing dot is kept.
  protected readonly editText = linkedSignal<number | null, string>({
    source: () => this.state().value(),
    computation: (value, previous) => {
      if (previous && this.parseDecimal(previous.value) === value) {
        return previous.value;
      }
      return this.getDisplayText(value);
    },
  });

  private readonly keypadTarget: NumericKeypadTarget = {
    press: (key: string) => {
      const input = this.inputElRef()?.nativeElement;
      if (input) {
        this.onKeypadKey(key, input);
        this.revealField({ onlyWhenClipped: true });
      }
    },
    dismiss: () => {
      this.inputElRef()?.nativeElement.blur();
      this.keypad.close(this.keypadTarget);
    },
  };

  onInputFocus(): void {
    this.keypad.open(this.keypadTarget);
    this.revealField();
  }

  private revealField(options?: { onlyWhenClipped: boolean }): void {
    if (this.keypad.reservedSpacePx() === 0) {
      return;
    }

    const behavior: ScrollBehavior = prefersReducedMotion() ? 'auto' : 'smooth';
    afterNextRender(
      () => {
        const fieldRoot = this.fieldRootRef()?.nativeElement;
        if (
          !fieldRoot ||
          (options?.onlyWhenClipped && !this.isClipped(fieldRoot))
        ) {
          return;
        }
        scrollElementIntoView(fieldRoot, { block: 'end', behavior });
      },
      { injector: this.injector }
    );
  }

  private isClipped(fieldRoot: HTMLElement): boolean {
    const container = findScrollableAncestor(fieldRoot);
    return (
      container !== null &&
      fieldRoot.getBoundingClientRect().bottom >
        container.getBoundingClientRect().bottom
    );
  }

  onInputBlur(): void {
    this.state().markAsTouched();
    this.keypad.close(this.keypadTarget);
  }

  // Taps outside the input and keypad do not blur the input on mobile, so blur and close explicitly.
  // `close(target)` is a no-op unless this input owns the keypad.
  onDocumentPointerDown(event: Event): void {
    if (!this.isMobilePortrait()) {
      return;
    }
    const input = this.inputElRef()?.nativeElement;
    if (!input) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (!target || target === input) {
      return;
    }
    if (target.closest('.checklist-number-keypad-sheet')) {
      return;
    }
    input.blur();
    this.keypad.close(this.keypadTarget);
  }

  ngOnDestroy(): void {
    this.keypad.close(this.keypadTarget);
  }

  get isReadonly(): boolean {
    return this.state().disabled();
  }

  get text(): string {
    return this.getDisplayText(this.state().value());
  }

  get displayText(): string {
    return this.text || this.placeholder() || '';
  }

  get hasTrailingContent(): boolean {
    return (
      this.suffixText() !== '' ||
      (this.state().touched() && this.state().errors().length > 0)
    );
  }

  get errorMessage(): string {
    const kind = this.state().errors()[0]?.kind;
    if (!kind) {
      return '';
    }

    return (
      this.errorMessages()[kind] ??
      (kind === 'required'
        ? 'This is a required field'
        : 'This field is invalid')
    );
  }

  onInput(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    if (!input) {
      return;
    }

    const normalized = this.getNormalizedDecimal(input.value);
    if (normalized !== null) {
      this.editText.set(normalized);
      const parsedValue = this.parseDecimal(normalized);
      this.state().value.set(parsedValue);
      this.state().markAsDirty();
      if (input.value !== normalized) {
        input.value = normalized;
      }
      return;
    }

    const fallbackText = this.editText();
    const fallbackValue = this.parseDecimal(fallbackText);
    if (this.state().value() !== fallbackValue) {
      this.state().value.set(fallbackValue);
      this.state().markAsDirty();
    }
    input.value = fallbackText;
  }

  onKeypadKey(key: string, input: HTMLInputElement): void {
    if (input.readOnly) return;
    const current = input.value;
    let next: string;

    if (key === 'delete') {
      next = current.slice(0, -1);
    } else if (key === '-') {
      next = current.startsWith('-') ? current.slice(1) : `-${current}`;
    } else if (key === '.') {
      if (current.includes('.')) return;
      next = `${current}.`;
    } else {
      next = `${current}${key}`;
    }

    if (next === current) return;
    input.value = next;
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  private getNormalizedDecimal(
    value: string | null | undefined
  ): string | null {
    const trimmed = (value ?? '').trim().replace(',', '.');
    if (trimmed === '') {
      return '';
    }

    return /^-?\d*(\.\d*)?$/.test(trimmed) ? trimmed : null;
  }

  private parseDecimal(text: string): number | null {
    if (text === '') {
      return null;
    }
    const parsed = Number.parseFloat(text);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private getDisplayText(value: number | null | undefined): string {
    return typeof value === 'number' && Number.isFinite(value)
      ? String(value)
      : '';
  }
}
